import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { ConversionRateSectionTitle } from "app/pages/industry-analysis/overview/Market-research-overview/layouts/styled";
import { getMarketSharesQueryParams } from "app/pages/industry-analysis/players/market-shares/utils/utils";
import { type TrendsTableResult, useTrendsTableQuery } from "app/pages/industry-analysis/queries/marketTrends";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../consts";

interface IConversionRateSectionHeaderProps {
    /**
     * Single-mode primary category's ecommerce-domain count. When provided
     * together with the trends-query totalDomainsCount, the section renders
     * the "Based on X ecommerce domains out of Y in this industry." subtitle.
     */
    categoryDomainCount?: number;
    /** Renders a comparison-mode subtitle instead of the count-based one. */
    isComparison?: boolean;
    /** When in comparison mode, distinguishes the PoP variant. */
    isPeriodOverPeriod?: boolean;
}

const selectTotalCount = (trendsData: TrendsTableResult) => trendsData.totalCount;

export const ConversionRateSectionHeader = ({
    categoryDomainCount,
    isComparison = false,
    isPeriodOverPeriod = false,
}: IConversionRateSectionHeaderProps) => {
    const { translate } = useTranslate();
    const { data: totalDomainsCount } = useTrendsTableQuery(getMarketSharesQueryParams(), selectTotalCount);

    const subtitleText = (() => {
        if (isComparison) {
            return translate(
                isPeriodOverPeriod
                    ? CONVERSION_RATE_TRANSLATION_KEYS.sectionSubtitleComparisonPop
                    : CONVERSION_RATE_TRANSLATION_KEYS.sectionSubtitleComparison,
            );
        }
        if (categoryDomainCount != null && totalDomainsCount != null) {
            return translate(CONVERSION_RATE_TRANSLATION_KEYS.sectionSubtitle, {
                ecommerceCount: categoryDomainCount,
                totalCount: totalDomainsCount,
            });
        }
        return null;
    })();

    return (
        <Flex
            flexDirection="column"
            gap="4px"
        >
            <Flex
                alignItems="center"
                gap="8px"
            >
                <ConversionRateSectionTitle />
                <Badge
                    type="new"
                    data-automation="conversion-rate-section-title-badge"
                />
            </Flex>
            {subtitleText ? (
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                    data-automation="conversion-rate-section-subtitle"
                >
                    {subtitleText}
                </Text>
            ) : null}
        </Flex>
    );
};
